import CheckIcon from "@mui/icons-material/Check";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PaymentIcon from "@mui/icons-material/Payment";
import PrintIcon from "@mui/icons-material/Print";
import UndoIcon from "@mui/icons-material/Undo";
import {LoadingButton} from "@mui/lab";
import {Button, IconButton, Menu, MenuItem, Skeleton, Stack, Typography} from "@mui/material";
import SimpleDialog from "crqlar-react-common/dist/components/SimpleDialog";
import {Folio, FolioStatus} from "crqlar-react-common/dist/models/billing/folio.model";
import {FolioItem} from "crqlar-react-common/dist/models/billing/folioItem.model";
import {
    canCapturePreauthorizedFolio,
    canMarkAsPaid,
    canMarkAsUncollectable,
    canRefund,
    canReleaseFunds,
    canVoidFolio
} from "crqlar-react-common/dist/utils/folio.util";
import {ReactNode, useState} from "react";
import {useTranslation} from "react-i18next";
import DigitalSigningButton from "../../../../digitalSigning/share/DigitalSigningButton";
import DigitalSigningSignatureButton from "../../../../digitalSigning/share/DigitalSigningSignatureButton";
import {DigitalSigningFlow} from "../../../../digitalSigning/share/useDigitalSigning";
import {FolioFormat} from "./folioFormat";
import {lineAmounts} from "./folioLine.util";
import {folioTotalCents} from "./folioList.util";
import {FolioActionKind, FolioActions} from "./useFolioActions";
import {FolioPaymentState, NO_CHARGE} from "./useFolioPayment";

const BUTTON_SX = {minHeight: 40, flexShrink: 0};

type MoreAction = {key: string, label: string, onClick: () => void, danger?: boolean};

// The reservation dialog's footer row: the two clusters apart, each a row of its own on a phone
const FooterFrame = ({children}: {children: ReactNode}) => (
    <Stack
        component={'footer'}
        direction={'row'}
        alignItems={'center'}
        justifyContent={'space-between'}
        gap={2}
        sx={{flexShrink: 0, px: {xs: 2, md: 3}, py: 1.5, borderTop: 1, borderColor: 'divider', flexWrap: {xs: 'wrap', sm: 'nowrap'}}}
    >
        {children}
    </Stack>
);

// The right cluster: why the next step waits, then Print and the next step side by side - on a phone
// the hint takes a line of its own above them
const FooterEnd = ({hint, children}: {hint?: string, children: ReactNode}) => (
    <Stack
        direction={'row'}
        alignItems={'center'}
        justifyContent={'flex-end'}
        sx={{ml: 'auto', width: {xs: '100%', sm: 'auto'}, flexWrap: 'wrap', columnGap: 2, rowGap: 1}}
    >
        {!!hint && <Typography variant={'caption'} color={'text.secondary'}>{hint}</Typography>}
        <Stack direction={'row'} alignItems={'center'} gap={1}>{children}</Stack>
    </Stack>
);

interface Props {
    /** Undefined while the folio is read: a placeholder for the next step. */
    folio?: Folio
    /** Read-only users change nothing: their footer has Print - and, once the guest signed, the signature's icon. */
    readOnly: boolean
    /** The draft is saving: Finalize waits. */
    saving: boolean
    payment: FolioPaymentState
    actions: FolioActions
    digitalSigning: DigitalSigningFlow
    /** Tips already on the folio: confirming adds the guest's as well, so it asks first. */
    postedTips: FolioItem[]
    format: FolioFormat
    onFinalize: () => void
    /** The printout of what is saved, in a new tab - the draft is saved first. */
    onPrint: () => void
}

/**
 * The folio view's footer, laid out like the reservation dialog's. Left: the ⋮ menu with the folio's
 * other status changes, then digital signing's split button while nothing is signed - from the
 * signature on, the green icon in its place, which shows what the guest signed: checked before
 * Confirm and charge, viewed after it. Right: Print, then the one next step - Finalize on a draft,
 * Confirm and charge once the guest signed it (again for a confirm that stopped before it finalized
 * the draft), Charge once finalized, or capturing a preauthorization, refunding a payment. Read-only
 * users get the icon and Print. Confirming a tip on a folio that has one already asks first.
 */
const FolioFooter = ({folio, readOnly, saving, payment, actions, digitalSigning, postedTips, format, onFinalize, onPrint}: Props) => {
    const {t} = useTranslation();
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const [confirmDiscard, setConfirmDiscard] = useState(false);
    const [confirmSecondTip, setConfirmSecondTip] = useState(false);
    const busy = saving || actions.running || digitalSigning.busy;

    if (!folio) {
        return (
            <FooterFrame>
                <Skeleton variant={'rounded'} width={120} height={40} sx={{ml: 'auto'}}/>
            </FooterFrame>
        );
    }

    // Wherever the folio has an id - for read-only users too
    const print = folio.id ? (
        <Button variant={'text'} startIcon={<PrintIcon/>} onClick={onPrint} disabled={busy} sx={BUTTON_SX}>
            {t('Print')}
        </Button>
    ) : null;
    // What the guest signed, from the signature on - viewing it is for read-only users too
    const signature = digitalSigning.signed ? (
        <DigitalSigningSignatureButton signing={digitalSigning.signed} onClick={digitalSigning.showDetails}/>
    ) : null;
    if (readOnly) {
        if (!print && !signature) return null;
        return (
            <FooterFrame>
                {signature}
                {!!print && <FooterEnd>{print}</FooterEnd>}
            </FooterFrame>
        );
    }

    // Only a draft is ever signed
    const guestSigned = digitalSigning.state.kind === 'SIGNED';
    const request = (kind: FolioActionKind) => () => actions.request(kind);
    const candidates: [boolean, MoreAction][] = [
        [canMarkAsPaid(folio), {key: 'markAsPaid', label: t('Mark as Paid'), onClick: request('markAsPaid')}],
        [canMarkAsUncollectable(folio), {key: 'markAsUncollectable', label: t('Mark as Uncollectable'), onClick: request('markAsUncollectable')}],
        [canReleaseFunds(folio), {key: 'release', label: t('Release funds'), onClick: request('release')}],
        [canVoidFolio(folio), {key: 'void', label: t('Void folio'), onClick: request('void'), danger: true}],
        // A wrong tip, a guest who wants to sign again: without the signature the folio can be finalized
        // or get a new link
        [guestSigned, {key: 'discardSignature', label: t('Discard signature'), onClick: () => setConfirmDiscard(true), danger: true}],
    ];
    const more = candidates.filter(([available]) => available).map(([, action]) => action);
    const offersSigning = digitalSigning.offersLink;
    // The guest's tip would be the folio's second: e.g. staff booked one by hand before the guest signed
    const secondTip = postedTips.length > 0 && digitalSigning.pendingTipCents > 0;
    const confirmGuest = () => (secondTip ? setConfirmSecondTip(true) : void digitalSigning.confirm());

    const primary = (): ReactNode => {
        // A confirm that stopped before it finalized the draft is finished the same way
        if (guestSigned || digitalSigning.confirmIncomplete) {
            return (
                // Green: the guest's tip waits for staff to accept it. A signature gone stale can only
                // fail again - the header's chip leads to the new link
                <LoadingButton
                    variant={'contained'}
                    color={'success'}
                    startIcon={<CheckIcon/>}
                    onClick={confirmGuest}
                    loading={digitalSigning.busy}
                    disabled={digitalSigning.stale || busy}
                    sx={BUTTON_SX}
                >
                    {t('Confirm and charge')}
                </LoadingButton>
            );
        }
        if (folio.status === FolioStatus.DRAFT) {
            return (
                <LoadingButton variant={'contained'} startIcon={<CheckIcon/>} onClick={onFinalize} loading={saving} disabled={busy} sx={BUTTON_SX}>
                    {t('Finalize')}
                </LoadingButton>
            );
        }
        if (folio.status === FolioStatus.OPEN) {
            return (
                <LoadingButton
                    variant={'contained'}
                    startIcon={<PaymentIcon/>}
                    onClick={payment.charge}
                    loading={payment.charging}
                    disabled={!payment.canCharge || busy}
                    sx={BUTTON_SX}
                >
                    {t('Charge {{amount}}', {amount: format.money(folioTotalCents(folio))})}
                </LoadingButton>
            );
        }
        if (canCapturePreauthorizedFolio(folio)) {
            return (
                <LoadingButton variant={'contained'} startIcon={<PaymentIcon/>} onClick={request('capture')} disabled={busy} sx={BUTTON_SX}>
                    {t('Charge preauthorized')}
                </LoadingButton>
            );
        }
        if (canRefund(folio)) {
            return (
                <LoadingButton variant={'outlined'} startIcon={<UndoIcon/>} onClick={request('refund')} disabled={busy} sx={BUTTON_SX}>
                    {t('Refund')}
                </LoadingButton>
            );
        }
        return null;
    };

    const next = primary();
    // Why Charge waits
    const hint = folio.status === FolioStatus.OPEN && payment.canChooseProvider && payment.provider === NO_CHARGE
        ? t('Choose a payment method')
        : undefined;
    if (!print && !next && !more.length && !offersSigning && !signature) return null;

    return (
        <>
            <FooterFrame>
                {(!!more.length || offersSigning || !!signature) && (
                    <Stack direction={'row'} alignItems={'center'} gap={1} sx={{width: {xs: '100%', sm: 'auto'}}}>
                        {!!more.length && (
                            <IconButton
                                aria-label={t('More actions')}
                                aria-haspopup={'menu'}
                                onClick={event => setAnchor(event.currentTarget)}
                                disabled={busy}
                                sx={{width: 40, height: 40, flexShrink: 0}}
                            >
                                <MoreVertIcon/>
                            </IconButton>
                        )}
                        {offersSigning && <DigitalSigningButton flow={digitalSigning} disabled={saving || actions.running}/>}
                        {signature}
                    </Stack>
                )}

                {(!!print || !!next) && (
                    <FooterEnd hint={hint}>
                        {print}
                        {next}
                    </FooterEnd>
                )}
            </FooterFrame>

            <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
                {more.map(action => (
                    <MenuItem
                        key={action.key}
                        onClick={() => {
                            setAnchor(null);
                            action.onClick();
                        }}
                        sx={{fontSize: 14, ...(action.danger && {color: 'error.main'})}}
                    >
                        {action.label}
                    </MenuItem>
                ))}
            </Menu>

            {/* The guest may have left already, so discarding asks first */}
            <SimpleDialog
                open={confirmDiscard}
                title={t('Discard the signature?')}
                message={t('The guest has to sign again with a new link.')}
                confirmText={t('Discard signature')}
                cancelText={t('Cancel')}
                onClose={() => setConfirmDiscard(false)}
                onConfirm={() => {
                    setConfirmDiscard(false);
                    void digitalSigning.voidLatest();
                }}
            />

            {/* Two tips are rarely meant: which ones there are, and what confirming adds */}
            <SimpleDialog
                open={confirmSecondTip}
                title={t('Add another tip?')}
                message={t("This folio already has a tip line ({{lines}}). Confirming adds the guest's tip of {{amount}} as well.", {
                    count: postedTips.length,
                    lines: postedTips.map(line => [line.description?.trim() || t('Tip'), format.money(lineAmounts(line).grossCents)].join(' ')).join(', '),
                    amount: format.money(digitalSigning.pendingTipCents),
                })}
                confirmText={t('Confirm anyway')}
                cancelText={t('Cancel')}
                onClose={() => setConfirmSecondTip(false)}
                onConfirm={() => {
                    setConfirmSecondTip(false);
                    void digitalSigning.confirm();
                }}
            />
        </>
    );
};

export default FolioFooter;
